import { Suspense, lazy, ElementType, useEffect } from 'react';
import { Navigate, useRoutes, useLocation, Outlet } from 'react-router-dom';
// hooks
import useAuth from '../hooks/useAuth';
// layouts
import DashboardLayout from '../layouts/dashboard';
import ProgramsLayout from 'src/layouts/ProgramsLayout';
// pages
import Report from 'src/pages/reporting/Report';
// guards
import GuestGuard from '../guards/GuestGuard';
import AuthGuard from '../guards/AuthGuard';
// import RoleBasedGuard from '../guards/RoleBasedGuard';
// components
import LoadingScreen from '../components/LoadingScreen';
import Plans from "../pages/saasSubscribe/PlansPage"; 
import { TransactionProvider } from 'src/contexts/TransactionContext';
import { CustomerDataType, TransactionOwnershipType } from 'src/@types/transactions';
import Scrollbar from 'src/components/Scrollbar';
import { CalculatorMode } from 'src/@types/emission';
import useMerchant from 'src/hooks/useMerchant';

// ----------------------------------------------------------------------

const Loadable = (Component: ElementType) => (props: any) => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { pathname } = useLocation();

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { isAuthenticated } = useAuth();

  const isDashboard = pathname.includes('/') && isAuthenticated;

  return (
    <Suspense fallback={<LoadingScreen isDashboard={isDashboard} />}>
      <Component {...props} />
    </Suspense>
  );
};

// delete expired local storage data
(() => {
  const raw = localStorage.getItem('cart');
  let savedCart = null;

  if (raw) {
    const { value, expiry } = JSON.parse(raw);
    if (Date.now() < expiry) {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      savedCart = value; // still valid
    } else {
      localStorage.removeItem('cart'); // expired
    }
  }
})();

export default function Router() {
  const { pathname } = useLocation();
  const { isMerchantLoading } = useMerchant();

  const delay = (ms: number) => new Promise(
      resolve => setTimeout(resolve, ms)
  );

  useEffect(() => {
    async function sendPageViewWithDelaySoThatPageTitleUpdated() {
      await delay(2000);
      // @ts-ignore
      window._hsq?.push(['setPath', pathname]);
      // @ts-ignore
      window._hsq?.push(['trackPageView']);
    }
    sendPageViewWithDelaySoThatPageTitleUpdated();

  }, [pathname]);

  const routesHelper = (pathname: string) => {
    if (pathname.includes('receipt') || pathname.includes('certificate') || pathname.includes('report')) {
      return <Outlet />;
    } else {
      return (
        <Scrollbar>
          <DashboardLayout />
        </Scrollbar>
      )
    }
  }

  const routes = useRoutes([
    {
      path: '/auth',
      children: [
        {
          path: 'login',
          element: (
            <GuestGuard>
              <Login />
            </GuestGuard>
          ),
        },
        {
          path: 'register',
          element: (
            <GuestGuard>
              <Register />
            </GuestGuard>
          ),
        },
        { path: 'login-unprotected', element: <Login /> },
        { path: 'register-unprotected', element: <Register /> },
        { path: 'reset-password', element: <ResetPassword /> },
        { path: 'new-password', element: <NewPassword /> },
        { path: 'verify', element: <VerifyCode /> },
      ],
    },

    // Dashboard Routes
    {
      path: '/',
      element: (
        <AuthGuard>
          {routesHelper(pathname)}
        </AuthGuard>
      ),
      children: [
        { element: <Navigate to={'/dashboard'} replace />, index: true },
        { path: 'dashboard', element: <GeneralApp /> },

        { path: 'programmes/business-customers', element: <BusinessCustomersPage /> },
        { path: 'programmes/business-customers/:id/edit', element: <BusinessCustomersEditPage /> },
        { path: 'programmes/business-customers/create', element: <BusinessCustomersCreatePage /> },

        { path: 'programmes/toolkit', element: <ToolkitPage /> },
        { path: 'programmes/toolkit/flight-calculator', element: <FlightCalculatorSettings /> },
        { path: 'programmes/toolkit/:code', element: <ToolkitItemPage /> },
        { path: 'programmes/individuals', element: <IndividualsPage /> },
        { path: 'programmes/:type/:code/impact/:dateFrom/:dateTo/report', element: <Report ownershipTypes={[TransactionOwnershipType.CUSTOMER]} /> },

        { path: 'my-offsets/transactions', element: <TransactionsPage /> },
        { path: 'my-offsets/transactions/:id', element: <Transaction /> },

        { path: 'my-offsets/footprints', element: <FootprintsPage /> },

        // EVENT:
        { path: 'my-offsets/footprints/event-amount', element: <BusinessCalculator mode={CalculatorMode.GENERIC_EVENT} isAmountOnly={true} /> },
        { path: 'my-offsets/footprints/event-amount/:footprintId', element: <BusinessCalculator mode={CalculatorMode.GENERIC_EVENT} isAmountOnly={true} /> },
        { path: 'my-offsets/footprints/event-amount/:footprintId/edit', element: <BusinessCalculator isEditPage={true} mode={CalculatorMode.GENERIC_EVENT} isAmountOnly={true} /> },
        
        { path: 'my-offsets/footprints/event-calculator', element: <BusinessCalculator mode={CalculatorMode.GENERIC_EVENT} /> },
        { path: 'my-offsets/footprints/event-calculator/:footprintId', element: <BusinessCalculator mode={CalculatorMode.GENERIC_EVENT} /> },
        { path: 'my-offsets/footprints/event-calculator/:footprintId/edit', element: <BusinessCalculator isEditPage={true} mode={CalculatorMode.GENERIC_EVENT} /> },

        // BUSINESS:
        { path: 'my-offsets/footprints/business-amount', element: <BusinessCalculator mode={CalculatorMode.BUSINESS} isAmountOnly={true} /> },
        { path: 'my-offsets/footprints/business-amount/:footprintId', element: <BusinessCalculator mode={CalculatorMode.BUSINESS} isAmountOnly={true} /> },
        { path: 'my-offsets/footprints/business-amount/:footprintId/edit', element: <BusinessCalculator isEditPage={true} mode={CalculatorMode.BUSINESS} isAmountOnly={true} /> },

        { path: 'my-offsets/footprints/business-calculator', element: <BusinessCalculator mode={CalculatorMode.BUSINESS}/> },
        { path: 'my-offsets/footprints/business-calculator/:footprintId', element: <BusinessCalculator mode={CalculatorMode.BUSINESS}/> },
        { path: 'my-offsets/footprints/business-calculator/:footprintId/edit', element: <BusinessCalculator isEditPage={true} mode={CalculatorMode.BUSINESS}/> },

        // Cart
        { path: 'cart', element: <CartPage /> },
        { path: 'checkout', element: <CheckoutPage /> },
        { path: 'checkout/complete/:id', element: <ConfirmationPage /> },
        { path: 'cart/request-quote', element: <RequestQuotePage /> },

        //

        { path: 'projects', element: <Projects /> },
        { path: 'projects/:projectCode', element: <ProjectPage /> },

        { path: 'portfolios', element: <Baskets /> },
        { path: 'portfolios/:basketId', element: <BasketPage /> },

        //

        {
          path: 'credits',
          children: [
            { element: <Navigate to="/credits/purchase-credits" replace />, index: true },
            { path: 'purchase', element: <Purchase /> },
          ]
        },
        {
          path: 'communications',
          children: [
            { element: <Navigate to="/communications" replace />, index: true },
            { path: 'impact', element: <MerchantImpact /> },
            { path: 'supported-projects', element: <MerchantProjects /> },

            { path: 'pages', element: <SustainabilityPages /> },
            { path: 'pages/create', element: <TlpSettingsPage /> },
            { path: 'pages/update', element: <TlpSettingsPage /> },

            { path: 'widgets', element: <ImpactStoriesPage /> },
            { path: 'widgets/:code', element: <ToolkitItemPage /> },
            { path: 'widgets/impact-widget', element: <ImpactWidgetPage /> },
            { path: 'impact/:dateFrom/:dateTo/report', element: <Report /> },
          ],
        },
        {
          path: 'programmes',
          element: (
              <TransactionProvider>
                <ProgramsLayout />
              </TransactionProvider>
          ),
          children: [
            { element: <Navigate to="/programmes" replace />, index: true },

            { path: 'customer/contributions', element: <Transaction /> },

            { path: ':type/:code/employees', element: <Customers type={CustomerDataType.EMPLOYEE} /> },
            { path: ':type/:code/emissions', element: <FlightCorporateEmissionsPage /> },
            { path: ':type/:code/emissions/calculate', element: <FlightCorporateEmissionsCalculatePage /> },
            { path: ':type/:code/emissions/calculate/:emissionRecordId', element: <FlightCorporateEmissionsCalculatePage /> },
            { path: ':type/:code/emissions/:emissionRecordId', element: <FlightsEstimatesPage /> },
            { path: ':type/:code/configuration', element: <MyCCSettingsPage /> },
            { path: ':type/:code/customers', element: <Customers type={CustomerDataType.CUSTOMER} /> },
            { path: ':type/:code/flights', element: <FlightsPage /> },
            { path: ':type/:code/impact', element: <Impact ownershipTypes={[TransactionOwnershipType.CUSTOMER]} /> },
            { path: ':type/impact', element: <Impact ownershipTypes={[TransactionOwnershipType.MERCHANT]} /> },
            { path: ':type/:code/engagement', element: <Engagement /> },
            // easier to handle multiple programme types when props come from useParams
            { path: ':type/:code/transactions', element: <Transactions />, },
            { path: ':type/:code/transactions/:id', element: <Transaction />, index: true },
          ],
        },
        {
          path: 'account',
          children: [
            { element: <Navigate to="/account" replace />, index: true },
            { path: 'plans', element: <Plans /> },
            { path: 'account-details', element: <AccountPage /> },
            { path: 'user-management', element: <UserManagement /> },
            { path: '/account/plans/confirmation', element: <PlanConfirmation /> },
            { path: '/account/plans/confirmation/cancel', element: <PlanConfirmation isCancel={true} /> },
          ],
        },
        {
          path: 'settings',
          children: [
            { element: <Navigate to="/settings" replace />, index: true },
            { path: 'theme', element: <ThemeSettingsPage /> },
            { path: 'business', element: <SettingsBusinessPage /> },
            { path: 'default-basket', element: <SettingsDefaultBasketPage /> },
            { path: 'billing', element: <SettingsBillingPage /> },
            { path: 'emails', element: <SettingsEmailsPage /> },
            { path: 'dashboard', element: <SettingsDashboardPage /> },
          ],
        },
        { path: 'permission-denied', element: <PermissionDenied /> },
        { path: '500', element: <Page500 /> },
      ],
    },

    // Main Routes
    {
      path: '*',
      // element: <LogoOnlyLayout />,
      children: [
        // DO NOT update this path without checking with customers that use it. 
        // (Known customer that uses this path: Etihad for Business)
        { path: 'transactions/:id/certificate', element: <Certificate /> },

        { path: 'transactions/:id/detailed-certificate', element: <DetailedCertificate /> },
        { path: 'transactions/:id/receipt', element: <EmailReceipt /> },
        { path: 'coming-soon', element: <ComingSoon /> },
        { path: 'maintenance', element: <Maintenance /> },
        { path: '404', element: <Page404 /> },
        { path: '403', element: <Page403 /> },
        { path: '*', element: <Navigate to="/404" replace /> },
      ],
    },
    {
      path: '/',
      element: <Navigate to="/auth/register" replace />,
    },
    { path: '*', element: <Navigate to="/404" replace /> },
  ]);

   // conditionally render loading screen or routes
   if (isMerchantLoading) {
    return <LoadingScreen />;
  }

  return routes;
}

// AUTHENTICATION
const Login = Loadable(lazy(() => import('../pages/auth/Login')));
const Register = Loadable(lazy(() => import('../pages/auth/Register')));
const ResetPassword = Loadable(lazy(() => import('../pages/auth/ResetPassword')));
const NewPassword = Loadable(lazy(() => import('../pages/auth/NewPassword')));
const VerifyCode = Loadable(lazy(() => import('../pages/auth/VerifyCode')));

// DASHBOARD

// GENERAL
const GeneralApp = Loadable(lazy(() => import('../pages/dashboard/GeneralApp')));
const BusinessCustomersPage = Loadable(lazy(() => import('../pages/business-customers')));
const BusinessCustomersEditPage = Loadable(lazy(() => import('../pages/business-customers/edit')));
const BusinessCustomersCreatePage = Loadable(lazy(() => import('../pages/business-customers/create')));

const CartPage = Loadable(lazy(() => import('../pages/purchase/cart')));
const CheckoutPage = Loadable(lazy(() => import('../pages/purchase/checkout')));
const ConfirmationPage = Loadable(lazy(() => import('../pages/purchase/confirmation')));
const RequestQuotePage = Loadable(lazy(() => import('../pages/purchase/request-quote')));

// USER
const AccountPage = Loadable(lazy(() => import('../pages/account/account')));
const PlanConfirmation = Loadable(lazy(() => import('../pages/saasSubscribe/PlanConfirmation')));
const UserManagement = Loadable(lazy(() => import('../pages/dashboard/UserManagement')));
const FlightCalculatorSettings = Loadable(lazy(() => import('../pages/engage/toolkit/flightCalculator/FlightCalculatorSettings')));

// SETTINGS
const ThemeSettingsPage = Loadable(lazy(() => import('../pages/settings/ThemeSettingsPage')));
const SettingsBusinessPage = Loadable(lazy(() => import('../pages/settings/business')));
const SettingsDefaultBasketPage = Loadable(lazy(() => import('../pages/settings/default-basket')));
const SettingsBillingPage = Loadable(lazy(() => import('../pages/settings/billing')));
const SettingsEmailsPage = Loadable(lazy(() => import('../pages/settings/emails')));
const SettingsDashboardPage = Loadable(lazy(() => import('../pages/settings/dashboard')));

// OFFSET
const Transactions = Loadable(lazy(() => import('../pages/Transactions/Transactions')));
// const PurchaseHistory = Loadable(lazy(() => import('../pages/Transactions/PurchaseHistory')));
const Transaction = Loadable(lazy(() => import('../pages/Transactions/Transaction')));
const Purchase = Loadable(lazy(() => import('../pages/purchase/PurchaseOneOff')));
const BusinessCalculator = Loadable(lazy(() => import('../pages/business-calculator/BusinessCalculator')));
const FlightCorporateEmissionsPage = Loadable(lazy(() => import('../pages/programmes/flight-corporate/emissions')));
const FlightCorporateEmissionsCalculatePage = Loadable(lazy(() => import('../pages/programmes/flight-corporate/calculate')));
const FlightsEstimatesPage = Loadable(lazy(() => import('../pages/programmes/flight-corporate/estimates')));

const IndividualsPage = Loadable(lazy(() => import('../pages/programmes/pages/individuals')));

const TransactionsPage = Loadable(lazy(() => import('../pages/my-offsets/pages/transactions')));
const FootprintsPage = Loadable(lazy(() => import('../pages/my-offsets/pages/footprints')));

const EmailReceipt = Loadable(lazy(() => import('../pages/documents/receipt/EmailReceipt')));
const Certificate = Loadable(lazy(() => import('../pages/documents/certificate')));
const DetailedCertificate = Loadable(lazy(() => import('../pages/documents/detailed-certificate')));
const Customers = Loadable(lazy(() => import('../pages/reporting/employee/Customers')));
const FlightsPage = Loadable(lazy(() => import('../pages/programmes/flight-corporate/flights')));

const Engagement = Loadable(lazy(() => import('../pages/reporting/Engagement')));
const Impact = Loadable(lazy(() => import('../pages/Impact')));
const MerchantImpact = Loadable(lazy(() => import('../pages/reporting/merchant/Impact')));
const MerchantProjects = Loadable(lazy(() => import('../pages/reporting/merchant/Projects')));

// ENGAGE
const ToolkitPage = Loadable(lazy(() => import('../pages/engage/ToolkitPage')));
const ToolkitItemPage = Loadable(lazy(() => import('../pages/engage/ToolkitItemPage')));
const SustainabilityPages = Loadable(lazy(() => import('../pages/engage/sustainabilityPages')));
const TlpSettingsPage = Loadable(lazy(() => import('../pages/engage/sustainabilityPages/configuration')));
const MyCCSettingsPage = Loadable(lazy(() => import('../pages/engage/experience/mycc/index')));
const ImpactWidgetPage = Loadable(lazy(() => import('../pages/engage/toolkit/impact-widget/impact-widget')));

// EXPLORE
const Baskets = Loadable(lazy(() => import('../pages/baskets/BasketsPage')));
const BasketPage = Loadable(lazy(() => import('../pages/baskets/BasketDetailsPage')));
const Projects = Loadable(lazy(() => import('../pages/projects/ProjectsPage')));
const ProjectPage = Loadable(lazy(() => import('../pages/projects/ProjectDetailsPage')));

// TEST RENDER PAGE BY ROLE
const PermissionDenied = Loadable(lazy(() => import('../pages/dashboard/PermissionDenied')));

// MAIN
const ComingSoon = Loadable(lazy(() => import('../pages/ComingSoon')));
const Maintenance = Loadable(lazy(() => import('../pages/Maintenance')));
const Page500 = Loadable(lazy(() => import('../pages/Page500')));
const Page403 = Loadable(lazy(() => import('../pages/Page403')));
const Page404 = Loadable(lazy(() => import('../pages/Page404')));

const ImpactStoriesPage = Loadable(lazy(() => import('../pages/engage/ImpactStoriesPage')));
